/**
 * 業務詳細の IF — 8 欄 ＋ 使われている場所（通る業務フローと操作する部品）＋ 受け入れ条件。
 *
 * `orpc-contract.ts` へは `getBusinessDetail` の名前で載っている。読むのは `spec_graph_*` である。
 *
 * **master と案件適用後の姿を 1 つの応答で返す。** 画面は案件を選んでいるとき「変わった欄」に
 * 印を出すので、2 本の姿が同時に要る。面（`before` / `after`）を入力に取る
 * `ElementPjSideSchema` を使わないのはこのためである — 面で分けると画面が同じ業務を 2 回引く。
 *
 * **ID はすべて number である。** 案件番号も number で、画面が `AIRCLOSET-` を前に付けて
 * 6 桁で出す（決定 2）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務詳細の入出力スキーマ。8 欄・使われている場所・受け入れ条件を master と案件適用後の姿の両方で返す形
 * @graph-connects none
 */
import { ACCEPTANCE_HYPOTHESIS_STATUSES, ACCEPTANCE_RULE_KINDS, BUSINESS_OUTPUT_COUNTS, COMPONENT_KINDS, FLOW_DESTINATION_KINDS, FLOW_TYPES, TERM_SECTIONS, } from '@cortex/domain-spec-graph';
import { z } from 'zod';
import { LocaleInputShape, NodeUpdatedAtSchema, SpecElementIdSchema, UntranslatedSchema, } from '../spec-graph-common.js';
/**
 * `getBusinessDetail` の入力。
 *
 * **面を取らない。** 出力が master と案件適用後の姿の両方を持つので、面で分けると同じ業務を
 * 2 回引くことになる。案件を省けば master だけを読む。
 * @graph-connects none
 */
export const BusinessDetailInputSchema = z.object({
    businessId: SpecElementIdSchema,
    pj: z.object({ number: z.number().int().min(1) }).optional(),
    ...LocaleInputShape,
});
/**
 * 欄に載る参照 1 件（アクター・ドメイン・用語・業務ルール・業務フロー・部品・画面）。
 *
 * 名前の材料が欠けていると表示名は空文字になる。**未翻訳を原文で埋めない**（決定 4）ので、
 * 空文字は「この言語では読めない」を意味する。
 * @graph-connects none
 */
export const BusinessDetailRefSchema = z.object({
    id: SpecElementIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
});
/**
 * 業務ルールの欄に載る参照 1 件。
 *
 * `changedInPj` は案件がこの業務ルールの中身を書き換えたかで、master の姿では常に false。
 * 結末との関わりは持たない — 結末は業務が持つ情報で、業務ルールの札にしない。
 * @graph-connects none
 */
export const BusinessDetailRuleRefSchema = BusinessDetailRefSchema.extend({
    changedInPj: z.boolean(),
});
/**
 * 名前の作りの目的語（用語の参照と区画）。区画が読めなければ `section` は null。
 * @graph-connects none
 */
export const BusinessDetailSubjectSchema = z.object({
    term: BusinessDetailRefSchema,
    section: z.enum(TERM_SECTIONS).nullable(),
});
/**
 * 結末の 1 行（結果の値・到達の印）。結果の値が行の鍵である。
 * @graph-connects none
 */
export const BusinessDetailEndingSchema = z.object({
    result: BusinessDetailRefSchema.nullable(),
    reachedMark: z
        .object({ property: BusinessDetailRefSchema, value: BusinessDetailRefSchema })
        .nullable(),
});
/**
 * この業務を通る業務フロー 1 本と、そのフローでの結末ごとの宛先。
 *
 * `routes` は業務の節点から出る枝で、`target` は宛先の節点が業務ならその業務（それ以外は null）。
 * `stage` は業務フローが属するステージ、`flowType` は業務フローの型（`main` がメイン、ほかは
 * サブの役割）。欄が読めなければどちらも null。
 * @graph-connects none
 */
export const BusinessDetailFlowUsageSchema = z.object({
    flow: BusinessDetailRefSchema,
    stage: BusinessDetailRefSchema.nullable(),
    flowType: z.enum(FLOW_TYPES).nullable(),
    routes: z.array(z.object({
        result: BusinessDetailRefSchema.nullable(),
        destinationKind: z.enum(FLOW_DESTINATION_KINDS),
        target: BusinessDetailRefSchema.nullable(),
    })),
});
/**
 * この業務を操作する部品 1 件と、その部品の種別（欄が読めなければ null）と、その部品を並べて
 * いる画面（無ければ空）。
 * @graph-connects none
 */
export const BusinessDetailComponentUsageSchema = z.object({
    component: BusinessDetailRefSchema,
    kind: z.enum(COMPONENT_KINDS).nullable(),
    screens: z.array(BusinessDetailRefSchema),
});
/**
 * 受け入れ条件のルール 1 本。why の欄は省かれていても空の並びで返し、画面が欄の有無を
 * 分岐しなくて済むようにする。優先の順位は、種類が `priority` のルールの並び順である。
 * @graph-connects none
 */
export const BusinessDetailAcceptanceRuleSchema = z.object({
    kind: z.enum(ACCEPTANCE_RULE_KINDS),
    text: z.string(),
    goals: z.array(z.string()),
    troubles: z.array(z.string()),
    measures: z.array(z.string()),
    hypotheses: z.array(z.object({ status: z.enum(ACCEPTANCE_HYPOTHESIS_STATUSES), text: z.string() })),
    evidences: z.array(z.object({ source: z.string(), quote: z.string() })),
    notes: z.array(z.string()),
    realizes: z.array(SpecElementIdSchema),
    needsReview: z.boolean(),
});
/**
 * 業務の受け入れ条件。目標の並びとルールの並び。
 * @graph-connects none
 */
export const BusinessDetailAcceptanceSchema = z.object({
    goals: z.array(z.object({ key: z.string(), text: z.string() })),
    rules: z.array(BusinessDetailAcceptanceRuleSchema),
});
/**
 * 業務 1 件の姿。8 欄 ＋ 使われている場所（通る業務フロー・操作する部品）＋ 受け入れ条件である。
 *
 * `acceptance` は受け入れ条件が書かれていなければ `null` である。
 *
 * `hasRules` は「業務ルールが 1 件以上か」である。**件数ではなく真偽で返す** — 画面が使うのは
 * 「業務ロジックを開くリンクを出すか」の判断だけで、件数は `rules` の長さが持つ。`rules` は
 * 集合で、並びの位置に意味は無い。
 * @graph-connects none
 */
export const BusinessDetailViewSchema = z.object({
    businessId: SpecElementIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    subject: BusinessDetailSubjectSchema.nullable(),
    verb: BusinessDetailRefSchema.nullable(),
    actor: BusinessDetailRefSchema.nullable(),
    domain: BusinessDetailRefSchema.nullable(),
    inputs: z.array(BusinessDetailRefSchema),
    executedAt: BusinessDetailRefSchema.nullable(),
    rules: z.array(BusinessDetailRuleRefSchema),
    endings: z.array(BusinessDetailEndingSchema),
    outputCount: z.enum(BUSINESS_OUTPUT_COUNTS).nullable(),
    outputClass: BusinessDetailRefSchema.nullable(),
    flows: z.array(BusinessDetailFlowUsageSchema),
    components: z.array(BusinessDetailComponentUsageSchema),
    hasRules: z.boolean(),
    acceptance: BusinessDetailAcceptanceSchema.nullable(),
    updatedAt: NodeUpdatedAtSchema,
});
/**
 * `getBusinessDetail` の出力。master と案件適用後の 2 つの姿である。
 *
 * `before` が `null` なのは、その案件がこの業務を**新しく作った**ことを意味する。`after` が
 * `null` なのは、その案件がこの業務を**消した**ことを意味する。どちらも失敗ではないので、
 * `NOT_FOUND` に倒さず `null` で運ぶ — 倒すと、案件が起こしたばかりの業務が案件モードで
 * 開けなくなる。
 *
 * **両方が `null` なら、その番号の業務は存在しない。** 画面はそこで未存在に倒す。
 * @graph-connects none
 */
export const BusinessDetailPairSchema = z.object({
    before: BusinessDetailViewSchema.nullable(),
    after: BusinessDetailViewSchema.nullable(),
});
//# sourceMappingURL=business-detail.js.map